var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useState, useLayoutEffect, } from 'react';
import shortid from 'shortid';
import { debounce, noop } from 'lodash-es';
import styled from '../../../styled';
import { Title, Message, MessageWrapper } from '../../input';
import DateInput from '../date/Input/DateInput';
import { Container, CalendarContainer } from '../date/DatePicker';
import { getDefaultPlaceholder, getDefaultInputTooltip } from './wording';
import TimerSelector from './TimerSelector';
import * as Icons from '../../icons';
const TimerDropdownContainer = styled(CalendarContainer) `
  padding: 0;
  overflow: hidden;
  ${({ reverse }) => (reverse ? 'top: auto; bottom:100%' : '')}
`;
const gridMaxHeight = 286;
function validTime(time) {
    if (!time || !/^\d{2}:\d{2}:\d{2}$/.test(time))
        return false;
    const [hour, minute, second] = time.split(':');
    return (parseInt(hour, 10) < 24 &&
        parseInt(minute, 10) < 60 &&
        parseInt(second, 10) < 60);
}
const useReverseDropdown = (inputRef) => {
    const [reverseDropdown, setReverseDropdown] = useState(false);
    useLayoutEffect(() => {
        // eslint-disable-next-line consistent-return
        const handleScroll = debounce(() => {
            if (typeof inputRef === 'function' || !inputRef || !inputRef.current)
                return undefined;
            const inputElement = inputRef.current;
            const currentPos = inputElement.getBoundingClientRect();
            const innerViewportHeight = window.innerHeight;
            const distanceToBottom = innerViewportHeight - currentPos.bottom;
            setReverseDropdown(distanceToBottom < gridMaxHeight);
        }, 300);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    return reverseDropdown;
};
const TimePicker = (_a) => {
    var { lang = 'en', inputRef = React.createRef(), inputWidth = 7.75, status = 'valid', title, renderMessages = [], block = false, disabled, placeholder, value, onChange = noop } = _a, restProps = __rest(_a, ["lang", "inputRef", "inputWidth", "status", "title", "renderMessages", "block", "disabled", "placeholder", "value", "onChange"]);
    const reverseDropdown = useReverseDropdown(inputRef);
    const [open, setOpen] = useState(false); // indicate that calendar is opened or not
    const [inputValue, setInputValue] = useState(() => {
        return validTime(value) ? value : '';
    });
    const handleInputClick = useCallback(() => setOpen(true), []);
    const handleBlur = useCallback(() => {
        if (!inputValue || validTime(inputValue)) {
            onChange(inputValue);
        }
        else {
            setInputValue(value);
        }
        setOpen(false); // close dropdown when blur
    }, [inputValue, onChange, value]);
    useEffect(() => {
        if (value && validTime(value)) {
            setInputValue(value);
        }
        else {
            setInputValue('');
        }
    }, [value]);
    return (React.createElement(Container, { block: block },
        title && React.createElement(Title, null, title),
        React.createElement(DateInput, Object.assign({ onBlur: handleBlur, lang: lang, ref: inputRef, value: inputValue, width: inputWidth, block: block, status: status, disabled: disabled, placeholder: placeholder || getDefaultPlaceholder(lang), onChange: e => {
                setInputValue(e.target.value);
                if (validTime(e.target.value)) {
                    onChange(e.target.value);
                }
            }, onClick: handleInputClick, onCalendarIconClick: handleInputClick, onClearBtnClick: () => {
                onChange('');
                if (typeof inputRef === 'function' || !inputRef || !inputRef.current)
                    return;
                inputRef.current.focus();
            }, tooltipText: getDefaultInputTooltip(lang), tooltipDirection: reverseDropdown ? 'BOTTOM_CENTER' : 'TOP_CENTER', Icon: Icons.Outline.Clock }, restProps)),
        React.createElement(MessageWrapper, null, renderMessages.map(renderMessage => (React.createElement(Message, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() }, renderMessage())))),
        open && (React.createElement(TimerDropdownContainer, { open: true, top: title ? 4.25 : 2.75, 
            // prevent dropdown close
            onMouseDown: e => e.preventDefault(), reverse: reverseDropdown },
            React.createElement(TimerSelector, { value: validTime(inputValue) ? inputValue : undefined, onTimeChange: time => {
                    setInputValue(time);
                    setOpen(false);
                    if (onChange)
                        onChange(time);
                } })))));
};
export default React.memo(React.forwardRef((props, ref) => {
    if (typeof ref === 'function')
        throw new Error('ref callback is not supported');
    return React.createElement(TimePicker, Object.assign({ inputRef: ref || React.createRef() }, props));
}));
//# sourceMappingURL=index.js.map